import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, CalendarDays, RefreshCw, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import accountApi from "@/lib/accountApiClient";
import { useAccount } from "@/lib/AccountContext";
import { accountDisplayName } from "@/lib/accountProfile";
import { localizedPath, useLang } from "@/lib/i18n";
import { fmtWIB } from "@/lib/dateFmt";
import useSEO from "@/lib/useSEO";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export function ResultRemoveControl({ resultId, t, removing, onRemove }) {
  const [confirming, setConfirming] = useState(false);
  if (!confirming) {
    return (
      <Button type="button" variant="ghost" onClick={() => setConfirming(true)} className="h-10 rounded-full text-muted-foreground hover:text-destructive" data-testid={`my-result-remove-${resultId}`}>
        <Trash2 className="h-4 w-4" /> {t("account.results.remove")}
      </Button>
    );
  }
  return (
    <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-3" data-testid={`my-result-remove-confirm-${resultId}`}>
      <p className="text-xs leading-relaxed text-muted-foreground">{t("account.results.remove_confirm")}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="destructive" disabled={removing} onClick={() => onRemove(resultId)} className="rounded-full">{removing ? t("account.working") : t("account.results.remove_action")}</Button>
        <Button type="button" size="sm" variant="ghost" disabled={removing} onClick={() => setConfirming(false)} className="rounded-full"><X className="h-3.5 w-3.5" /> {t("account.results.remove_cancel")}</Button>
      </div>
    </div>
  );
}

export async function removeSavedResult(client, resultId, onSuccess) {
  await client.delete(`/account/results/${encodeURIComponent(resultId)}`);
  onSuccess(resultId);
}

export function MyResultsContent({ results, lang, pick, t, onRemove = () => {}, removingId = "" }) {
  if (!results.length) {
    return (
      <section className="rounded-3xl border border-border bg-card px-6 py-14 text-center shadow-sm" data-testid="my-results-empty">
        <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-sage-100 text-teal-800"><BookOpen className="h-5 w-5" /></div>
        <h2 className="mt-5 font-display text-2xl">{t("account.results.empty_title")}</h2>
        <p className="mx-auto mt-2 max-w-lg text-sm leading-relaxed text-muted-foreground">{t("account.results.empty_description")}</p>
        <Button asChild className="mt-6 h-11 rounded-full bg-teal-800 px-6 text-cream-50 hover:bg-teal-700">
          <Link to={localizedPath("/instruments", lang)}>{t("account.results.browse")}</Link>
        </Button>
      </section>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid="my-results-list">
      {results.map((result) => {
        const instrumentName = pick(result.instrument || {}, "name") || t("account.results.untitled");
        return (
          <article key={result.id} className="flex min-w-0 flex-col rounded-2xl border border-border bg-card p-5 shadow-sm" data-testid={`my-result-${result.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="font-display text-xl leading-snug text-teal-900">{instrumentName}</h2>
                <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                  <CalendarDays className="h-3.5 w-3.5" />
                  <time dateTime={result.completed_at || undefined}>{fmtWIB(result.completed_at, lang)}</time>
                </div>
              </div>
              <Badge variant="outline" className="shrink-0 border-sage-200 bg-sage-50 text-[10px] uppercase tracking-wider text-teal-800">
                {t("account.results.saved")}
              </Badge>
            </div>
            {result.entitlement_required && (
              <p className="mt-4 rounded-xl bg-cream-100 p-3 text-xs leading-relaxed text-muted-foreground" data-testid="my-result-paid-note">
                {t("account.results.paid_note")}
              </p>
            )}
            <div className="mt-auto grid gap-2 pt-5">
              <Button asChild variant="outline" className="h-10 w-full rounded-full">
                <Link to={`/results/${encodeURIComponent(result.anonymous_code)}`} data-testid={`my-result-open-${result.id}`}>
                  {t("account.results.open")} <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <ResultRemoveControl resultId={result.id} t={t} removing={removingId === result.id} onRemove={onRemove} />
            </div>
          </article>
        );
      })}
    </div>
  );
}

export default function MyResults() {
  const { user, clearAccountSession } = useAccount();
  const { lang, pick, t } = useLang();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [removingId, setRemovingId] = useState("");

  useSEO({
    title: `${t("account.my_results")} — Mindovia`,
    description: t("account.results.description"),
    url: null,
    robots: "noindex, nofollow, noarchive",
  });

  const loadResults = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await accountApi.get("/account/results");
      setResults(Array.isArray(data?.results) ? data.results : []);
    } catch (requestError) {
      if ([401, 403].includes(requestError?.response?.status)) {
        clearAccountSession();
      } else {
        setError(t("account.results.error"));
      }
    } finally {
      setLoading(false);
    }
  }, [clearAccountSession, t]);

  useEffect(() => {
    loadResults();
  }, [loadResults]);

  const removeResult = async (resultId) => {
    setRemovingId(resultId);
    try {
      await removeSavedResult(accountApi, resultId, (removedId) => {
        setResults((current) => current.filter((result) => result.id !== removedId));
      });
      toast.success(t("account.results.removed"));
    } catch (requestError) {
      if ([401, 403].includes(requestError?.response?.status)) clearAccountSession();
      else toast.error(t("account.results.remove_error"));
    } finally {
      setRemovingId("");
    }
  };

  return (
    <div className="mx-auto min-h-[65vh] max-w-5xl px-4 py-10 sm:px-6 sm:py-14" data-wrap="responsive">
      <div className="mb-8">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta-700">{user ? t("account.hello", { name: accountDisplayName(user) }) : ""}</p>
        <h1 className="mt-2 font-display text-4xl tracking-tight sm:text-5xl">{t("account.my_results")}</h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">{t("account.results.description")}</p>
      </div>

      {loading ? (
        <div className="py-20 text-center text-sm text-muted-foreground" aria-live="polite" data-testid="my-results-loading">{t("account.loading")}</div>
      ) : error ? (
        <div className="rounded-2xl border border-destructive/30 bg-card p-6" role="alert" data-testid="my-results-error">
          <p className="text-sm text-destructive">{error}</p>
          <Button type="button" variant="outline" onClick={loadResults} className="mt-4 rounded-full">
            <RefreshCw className="h-4 w-4" /> {t("account.results.retry")}
          </Button>
        </div>
      ) : (
        <MyResultsContent results={results} lang={lang} pick={pick} t={t} onRemove={removeResult} removingId={removingId} />
      )}
    </div>
  );
}
